import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  LineChart,
  ComposedChart
} from 'recharts';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  IconButton
} from '@mui/material';
import { TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';

// Custom tooltip with glass morphism and enhanced information
const CustomTooltip = ({ active, payload, label, isMobile, chartType }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    
    return (
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: 3,
          boxShadow: '0 15px 35px rgba(0, 0, 0, 0.2)',
          p: isMobile ? 2 : 3,
          minWidth: 250,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: '8px solid rgba(255, 255, 255, 0.95)',
          }
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            mb: 1
          }}
        >
          {label}
        </Typography>
        
        {chartType === 'area' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: '#667eea',
                  mr: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Available: {data?.licenses || 0} licenses
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              💡 This shows how many licenses are still available to use
            </Typography>
          </>
        )}
        
        {chartType === 'composed' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: '#10B981',
                  mr: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Available: {data?.licenses || 0} licenses
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: '#f093fb',
                  mr: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Used: {data?.used || 0} licenses
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: '#8B5CF6',
                  mr: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Total: {data?.total || 0} licenses
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              💡 This shows both available and used licenses together
            </Typography>
          </>
        )}
        
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
          <Typography variant="caption" color="text.secondary">
            Usage: {data?.efficiency || 0}% of licenses are being used
          </Typography>
        </Box>
      </Box>
    );
  }
  return null;
};

// Animated gradient component
const AnimatedGradient = ({ children }) => {
  const [gradientId] = useState(`gradient-${Math.random().toString(36).substr(2, 9)}`);
  
  return (
    <Box sx={{ position: 'relative' }}>
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#667eea" stopOpacity={0.8}>
              <animate
                attributeName="stop-color"
                values="#667eea;#764ba2;#667eea"
                dur="4s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="50%" stopColor="#764ba2" stopOpacity={0.6}>
              <animate
                attributeName="stop-color"
                values="#764ba2;#f093fb;#764ba2"
                dur="4s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="100%" stopColor="#f093fb" stopOpacity={0.4}>
              <animate
                attributeName="stop-color"
                values="#f093fb;#667eea;#f093fb"
                dur="4s"
                repeatCount="indefinite"
              />
            </stop>
          </linearGradient>
        </defs>
      </svg>
      {children}
    </Box>
  );
};

const ModernAreaChart = ({ adminUsers }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [chartType, setChartType] = useState('area');

  // Use actual data from adminUsers - show all users
  const chartData = adminUsers.map((user, index) => ({
    name: user.USER_NAME?.substring(0, 8) || `User ${index + 1}`,
    licenses: user.REMAINING_LICENSES || 0,
    used: (user.TOTAL_LICENSES || 0) - (user.REMAINING_LICENSES || 0),
    efficiency: Math.round(((user.TOTAL_LICENSES || 0) - (user.REMAINING_LICENSES || 0)) / (user.TOTAL_LICENSES || 1) * 100),
    total: user.TOTAL_LICENSES || 0
  }));

  const getChartComponent = () => {
    switch (chartType) {
      case 'area':
        return (
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#667eea" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#667eea"/>
                <stop offset="100%" stopColor="#764ba2"/>
              </linearGradient>
            </defs>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={theme.palette.divider} 
              opacity={0.2}
            />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip isMobile={isMobile} chartType="area" />} />
            <Area
              type="monotone"
              dataKey="licenses"
              stroke="url(#lineGradient)"
              strokeWidth={3}
              fill="url(#areaGradient)"
              fillOpacity={0.8}
            />
          </AreaChart>
        );
      
      case 'composed':
        return (
          <ComposedChart data={chartData}>
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981"/>
                <stop offset="100%" stopColor="#059669"/>
              </linearGradient>
              <linearGradient id="lineGradient2" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#f093fb"/>
                <stop offset="100%" stopColor="#f5576c"/>
              </linearGradient>
            </defs>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={theme.palette.divider} 
              opacity={0.2}
            />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip isMobile={isMobile} chartType="composed" />} />
            <Area
              type="monotone"
              dataKey="licenses"
              stroke="url(#lineGradient2)"
              strokeWidth={2}
              fill="url(#barGradient)"
              fillOpacity={0.3}
            />
            <Line
              type="monotone"
              dataKey="used"
              stroke="url(#lineGradient2)"
              strokeWidth={3}
              dot={{ fill: '#f093fb', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#f093fb', strokeWidth: 2 }}
            />
          </ComposedChart>
        );
      
      default:
        return null;
    }
  };

  return (
    <Card 
      elevation={0}
      sx={{ 
        height: { xs: 450, md: 400 },
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: 3,
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
        }
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
        {/* Header with Chart Type Toggle */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: isMobile ? 2 : 3 
        }}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            component="h2"
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
              letterSpacing: '-0.5px'
            }}
          >
            License Usage Trends
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setChartType('area')}
              title="Show available licenses only"
              sx={{
                background: chartType === 'area' ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                color: chartType === 'area' ? '#667eea' : theme.palette.text.secondary,
                '&:hover': {
                  background: 'rgba(102, 126, 234, 0.1)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <TrendingUp size={18} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setChartType('composed')}
              title="Show both available and used licenses"
              sx={{
                background: chartType === 'composed' ? 'rgba(240, 147, 251, 0.1)' : 'transparent',
                color: chartType === 'composed' ? '#f093fb' : theme.palette.text.secondary,
                '&:hover': {
                  background: 'rgba(240, 147, 251, 0.1)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <BarChart3 size={18} />
            </IconButton>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3,
          flexWrap: 'wrap'
        }}>
          <Chip
            label={`${adminUsers.reduce((sum, user) => sum + (user.REMAINING_LICENSES || 0), 0)} Remaining`}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
          />
          <Chip
            label={`${adminUsers.reduce((sum, user) => sum + ((user.TOTAL_LICENSES || 0) - (user.REMAINING_LICENSES || 0)), 0)} Used`}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
          />
          <Chip
            label={`${adminUsers.reduce((sum, user) => sum + (user.TOTAL_LICENSES || 0), 0)} Total`}
            sx={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
          />
        </Box>

        {/* Chart Container */}
        <Box sx={{ 
          height: 250,
          position: 'relative',
          overflowX: 'auto',
          overflowY: 'hidden',
          '& .recharts-area': {
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
          },
          '& .recharts-line': {
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
          }
        }}>
          <Box sx={{ 
            width: Math.max(600, chartData.length * 80), // Dynamic width based on number of users
            height: '100%',
            minWidth: '100%'
          }}>
            <ResponsiveContainer width="100%" height="100%">
              {getChartComponent()}
            </ResponsiveContainer>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ModernAreaChart; 